import useSWR, { SWRResponse } from 'swr'

import { PLATFORM } from '@/constants/platform'
import { useAuthStore } from '@/stores/useAuthStore'
import { AwsProvision } from '@/types/streams/awsProvision'
import { EventPipelineStatus } from '@/types/streams/eventPipelineStatus'
import { Stream } from '@/types/streams/stream'
import formatResponseError from '@/utils/_legacy/formatResponseError'
import getResponseErrorMessage from '@/utils/_legacy/getResponseErrorMessage'
import { notifyError } from '@/utils/notifyError'

import { DGE_ENDPOINTS } from './constants/dgeEndpoints'
import { deleteData, fetchData, postData } from './request'

const FETCH_INTERVAL = 5000
const RETRY_COUNT = 6
const RETRY_INTERVAL = 5000

export async function getStreamData(eventId: string, streamingConfigId: string) {
  const platform = useAuthStore.getState().platform
  const token = useAuthStore.getState().token

  if (platform === PLATFORM.DGE) {
    if (!token) return

    const url = `${DGE_ENDPOINTS.Streaming.get(eventId)}?exchangeAuth=${encodeURIComponent(
      token
    )}&hlsv2=true&streamingConfigId=${streamingConfigId}`

    return fetchData<Stream>({ endpointData: { url }, noHeaders: true })
  }

  throw new Error('Unsupported platform')
}

export function useGetPipelineStatus(
  eventId: string | number,
  eventStreamingConfigId?: string | number
): SWRResponse<EventPipelineStatus> {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DGE) {
    const endpointData = eventStreamingConfigId
      ? DGE_ENDPOINTS.Streaming.Event.PipelineStatus.get(
          String(eventId),
          String(eventStreamingConfigId)
        )
      : null

    return useSWR<EventPipelineStatus>(endpointData, fetchData<EventPipelineStatus>, {
      refreshInterval: FETCH_INTERVAL,
      errorRetryCount: RETRY_COUNT,
      revalidateOnFocus: false,
      onErrorRetry: (err, _key, _config, revalidate, { retryCount }) => {
        const { status } = formatResponseError(err)
        if (status !== 404 || retryCount === RETRY_COUNT) {
          notifyError(getResponseErrorMessage(err))
          return
        }
        setTimeout(() => revalidate({ retryCount }), RETRY_INTERVAL)
      }
    })
  }

  throw new Error('Unsupported platform')
}

export function postProvision(eventId: string, awsRegion?: string | null) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DGE) {
    return postData<{ region: string | null | undefined }, AwsProvision>({
      endpointData: DGE_ENDPOINTS.Streaming.Event.Provision.post(eventId),
      data: { region: awsRegion }
    })
  }

  throw new Error('Unsupported platform')
}

export function deleteProvision(eventId: string, awsRegion: string) {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DGE) {
    return deleteData({
      endpointData: DGE_ENDPOINTS.Streaming.Event.Provision.delete(eventId, awsRegion)
    })
  }

  throw new Error('Unsupported platform')
}
